import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type TenantRow = { tenant_slug: string; tenant_name: string; country_code: string | null };
type WarRow = { tenant_slug: string; pulses: number };

export function TenantWarsWidget() {
  const [rows, setRows] = useState<(WarRow & Partial<TenantRow>)[]>([]);

  const load = async () => {
    const since = new Date(Date.now() - 1000 * 60 * 60).toISOString();
    const [{ data: pulses }, { data: tenants }] = await Promise.all([
      supabase
        .from("hype_pulses")
        .select("tenant_id, pulse_count")
        .gte("created_at", since)
        .not("tenant_id", "is", null)
        .limit(2000),
      supabase.from("tenants_config").select("tenant_slug, tenant_name, country_code"),
    ]);
    const agg = new Map<string, number>();
    for (const p of (pulses ?? []) as Array<{ tenant_id: string | null; pulse_count: number | null }>) {
      if (!p.tenant_id) continue;
      agg.set(p.tenant_id, (agg.get(p.tenant_id) ?? 0) + (p.pulse_count ?? 1));
    }
    const tMap = new Map(
      ((tenants ?? []) as TenantRow[]).map((t) => [t.tenant_slug, t]),
    );
    const merged: (WarRow & Partial<TenantRow>)[] = Array.from(agg.entries())
      .map(([slug, pulses]) => ({ tenant_slug: slug, pulses, ...(tMap.get(slug) ?? {}) }))
      .sort((a, b) => b.pulses - a.pulses)
      .slice(0, 5);
    setRows(merged);
  };

  useEffect(() => {
    void load();
    const id = setInterval(() => void load(), 30_000);
    return () => clearInterval(id);
  }, []);

  const max = Math.max(1, ...rows.map((r) => r.pulses));

  return (
    <section className="rounded-xl border border-border bg-card/70 backdrop-blur p-4 space-y-3">
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold tracking-wide">🌎 Tenant Wars</h3>
        <span className="text-[10px] uppercase text-muted-foreground">last hour</span>
      </header>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">Aún sin pulsos — sé el primero en empujar a tu país. 🔥</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r, i) => (
            <li key={r.tenant_slug} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium">
                  #{i + 1} {r.tenant_name ?? r.tenant_slug}
                  {r.country_code && <span className="ml-1 text-muted-foreground">· {r.country_code}</span>}
                </span>
                <span className="tabular-nums">{r.pulses.toLocaleString()}</span>
              </div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(r.pulses / max) * 100}%`,
                    background: "var(--tenant-accent-glow, #3B82F6)",
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}